import * as React from "react";

import { cn } from "@/lib/utils/cn";

interface ProgressRingProps {
  /** Completion, 0–100. Clamped. */
  value: number;
  /** Outer diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  strokeWidth?: number;
  className?: string;
  /** Centre content (e.g. the percentage). */
  children?: React.ReactNode;
  /**
   * When true (default) the arc uses the four-theme spectrum gradient — reads as
   * "the whole diagnostic". Pass false to use a single colour via `indicatorClassName`.
   */
  spectrum?: boolean;
  /** Stroke class for the arc when `spectrum` is false (e.g. "stroke-primary"). */
  indicatorClassName?: string;
}

/**
 * Circular progress indicator. The default spectrum arc is the app's identity
 * mark for overall self-diagnostic completion; single-colour mode suits a
 * per-thematic ring.
 */
export function ProgressRing({
  value,
  size = 116,
  strokeWidth = 12,
  className,
  children,
  spectrum = true,
  indicatorClassName,
}: ProgressRingProps) {
  const gradientId = React.useId();
  const pct = Math.max(0, Math.min(100, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct / 100);

  return (
    <div
      className={cn("relative inline-grid place-items-center", className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="-rotate-90"
        role="img"
        aria-label={`${Math.round(pct)}%`}
      >
        {spectrum && (
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="hsl(var(--theme-governance))" />
              <stop offset="0.4" stopColor="hsl(var(--theme-economic))" />
              <stop offset="0.7" stopColor="hsl(var(--theme-social))" />
              <stop offset="1" stopColor="hsl(var(--theme-environment))" />
            </linearGradient>
          </defs>
        )}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className="stroke-muted"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          stroke={spectrum ? `url(#${gradientId})` : undefined}
          className={cn(
            "transition-[stroke-dashoffset] duration-700 ease-out motion-reduce:transition-none",
            !spectrum && (indicatorClassName ?? "stroke-primary"),
          )}
        />
      </svg>
      {children != null && (
        <div className="absolute inset-0 grid place-items-center text-center">
          {children}
        </div>
      )}
    </div>
  );
}
